import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const presets = [{ id: "original", label: "Original" }, { id: "square", label: "Square 1:1", ratio: 1 }, { id: "portrait", label: "Portrait 4:5", ratio: 4 / 5 }, { id: "landscape", label: "Landscape 16:9", ratio: 16 / 9 }];
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export const CoverCropDialog = ({ src, initialCrop, credit, onApply, onCancel }) => {
  const [image, setImage] = useState(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [preset, setPreset] = useState("original");
  const [zoom, setZoom] = useState(1);
  const [focus, setFocus] = useState({ x: 0.5, y: 0.5 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const resizeObserverRef = useRef(null);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);

  const attachViewport = useCallback((node) => {
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;
    if (!node) return;
    setSize({ width: node.clientWidth, height: node.clientHeight });
    const observer = new ResizeObserver(([entry]) => setSize({ width: entry.contentRect.width, height: entry.contentRect.height }));
    resizeObserverRef.current = observer;
    observer.observe(node);
  }, []);

  useEffect(() => {
    let active = true;
    const photo = new Image();
    photo.onload = () => {
      if (!active) return;
      setImage(photo);
      const naturalAspect = photo.naturalWidth / photo.naturalHeight;
      if (initialCrop) {
        setPreset(presets.find((entry) => entry.ratio && Math.abs(entry.ratio - initialCrop.aspect) < 0.01)?.id || "original");
        setZoom(clamp(Math.min(1, initialCrop.aspect / naturalAspect) / initialCrop.width, 1, 3));
        setFocus({ x: initialCrop.x + initialCrop.width / 2, y: initialCrop.y + initialCrop.height / 2 });
      }
    };
    photo.onerror = () => { if (active) setError("Could not load this photo. Go back and choose another image."); };
    photo.src = src;
    return () => { active = false; };
  }, [src, initialCrop]);

  const view = useMemo(() => {
    if (!image || !size.width || !size.height) return null;
    const aspect = presets.find((entry) => entry.id === preset)?.ratio || image.naturalWidth / image.naturalHeight;
    const width = Math.min(size.width - 28, (size.height - 28) * aspect);
    const height = width / aspect;
    const x = (size.width - width) / 2;
    const y = (size.height - height) / 2;
    const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight) * zoom;
    const imageWidth = image.naturalWidth * scale;
    const imageHeight = image.naturalHeight * scale;
    const focusX = clamp(focus.x, width / imageWidth / 2, 1 - width / imageWidth / 2);
    const focusY = clamp(focus.y, height / imageHeight / 2, 1 - height / imageHeight / 2);
    return { x, y, width, height, aspect, imageWidth, imageHeight, focusX, focusY, left: x + width / 2 - focusX * imageWidth, top: y + height / 2 - focusY * imageHeight };
  }, [image, size, preset, zoom, focus]);

  useEffect(() => {
    if (!view || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(size.width * ratio);
    canvas.height = Math.round(size.height * ratio);
    const context = canvas.getContext("2d");
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.fillStyle = "#1c1917";
    context.fillRect(0, 0, size.width, size.height);
    context.drawImage(image, view.left, view.top, view.imageWidth, view.imageHeight);
    context.fillStyle = "rgba(12,10,9,0.62)";
    context.fillRect(0, 0, size.width, view.y);
    context.fillRect(0, view.y + view.height, size.width, size.height - view.y - view.height);
    context.fillRect(0, view.y, view.x, view.height);
    context.fillRect(view.x + view.width, view.y, size.width - view.x - view.width, view.height);
    context.strokeStyle = "rgba(255,255,255,0.9)";
    context.lineWidth = 1.5;
    context.strokeRect(view.x, view.y, view.width, view.height);
    context.strokeStyle = "rgba(255,255,255,0.25)";
    context.lineWidth = 1;
    for (let line = 1; line < 3; line++) {
      context.beginPath();
      context.moveTo(view.x + view.width * line / 3, view.y);
      context.lineTo(view.x + view.width * line / 3, view.y + view.height);
      context.moveTo(view.x, view.y + view.height * line / 3);
      context.lineTo(view.x + view.width, view.y + view.height * line / 3);
      context.stroke();
    }
  }, [view, image, size]);

  const dragStart = (event) => {
    if (!view || saving) return;
    event.preventDefault();
    event.currentTarget.focus();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { x: event.clientX, y: event.clientY, focusX: view.focusX, focusY: view.focusY };
  };
  const dragMove = (event) => {
    const start = dragRef.current;
    if (!start || !view || saving) return;
    setFocus({ x: clamp(start.focusX - (event.clientX - start.x) / view.imageWidth, view.width / view.imageWidth / 2, 1 - view.width / view.imageWidth / 2), y: clamp(start.focusY - (event.clientY - start.y) / view.imageHeight, view.height / view.imageHeight / 2, 1 - view.height / view.imageHeight / 2) });
  };
  const moveWithKeys = (event) => {
    if (!view || saving || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
    event.preventDefault();
    const step = event.shiftKey ? 0.05 : 0.01;
    setFocus({ x: clamp(view.focusX + (event.key === "ArrowLeft" ? -step : event.key === "ArrowRight" ? step : 0), 0, 1), y: clamp(view.focusY + (event.key === "ArrowUp" ? -step : event.key === "ArrowDown" ? step : 0), 0, 1) });
  };
  const reset = () => { setPreset("original"); setZoom(1); setFocus({ x: 0.5, y: 0.5 }); };
  const save = async () => {
    if (!view || saving) return;
    const width = Math.min(1, view.width / view.imageWidth);
    const height = Math.min(1, view.height / view.imageHeight);
    const crop = width > 0.99999 && height > 0.99999 ? null : { x: clamp((view.x - view.left) / view.imageWidth, 0, 1 - width), y: clamp((view.y - view.top) / view.imageHeight, 0, 1 - height), width, height, aspect: view.aspect };
    setSaving(true); setError("");
    try { await onApply(crop); }
    catch (err) { setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not save this crop. Please try again."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !saving) onCancel(); }}>
      <DialogContent data-testid="bucket-crop-dialog" className="w-[calc(100%-1.5rem)] max-w-xl max-h-[90vh] overflow-y-auto rounded-3xl p-4 sm:p-5 gap-3 font-['Figtree']" onCloseAutoFocus={(event) => event.preventDefault()} onEscapeKeyDown={(event) => { event.stopPropagation(); if (saving) event.preventDefault(); }}>
        <DialogHeader className="text-left pr-6"><DialogTitle data-testid="crop-heading" className="font-['Nunito'] text-xl">Frame your cover</DialogTitle><DialogDescription data-testid="crop-description" className="text-xs">Drag the photo and adjust the zoom. Your original stays untouched.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {presets.map((entry) => <Button key={entry.id} type="button" data-testid={`crop-preset-${entry.id}`} aria-pressed={preset === entry.id} disabled={saving || !image} variant="outline" onClick={() => { setPreset(entry.id); setZoom(1); setFocus({ x: 0.5, y: 0.5 }); }} className={`rounded-full px-2 text-xs ${preset === entry.id ? "bg-ac-soft text-ac-softfg border-ac" : ""}`}>{entry.label}</Button>)}
        </div>
        <div ref={attachViewport} className="relative h-56 sm:h-72 overflow-hidden rounded-2xl bg-stone-900">
          <canvas ref={canvasRef} data-testid="bucket-crop-canvas" tabIndex={0} role="img" aria-label="Crop preview. Drag to move the photo, or use arrow keys when focused." onPointerDown={dragStart} onPointerMove={dragMove} onPointerUp={() => { dragRef.current = null; }} onPointerCancel={() => { dragRef.current = null; }} onKeyDown={moveWithKeys} className="block h-full w-full touch-none cursor-grab active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white" />
          {!image && !error && <span data-testid="crop-loading" role="status" className="absolute inset-0 grid place-items-center text-white"><Loader2 className="w-5 h-5 animate-spin" aria-label="Loading photo" /></span>}
        </div>
        {credit}
        <div className="space-y-1">
          <div className="flex items-center justify-between"><Label htmlFor="crop-zoom-slider" data-testid="crop-zoom-label" className="text-xs font-bold text-stone-600">Zoom</Label><span data-testid="crop-zoom-value" className="text-xs text-stone-500">{Math.round(zoom * 100)}%</span></div>
          <input id="crop-zoom-slider" data-testid="crop-zoom-slider" type="range" min="1" max="3" step="0.01" value={zoom} disabled={saving || !image} onChange={(event) => setZoom(Number(event.target.value))} className="w-full accent-[var(--ac)] cursor-pointer" />
        </div>
        {error && <p data-testid="crop-error" role="alert" className="text-sm text-rose-600 break-words">{error}</p>}
        <DialogFooter className="flex-row flex-wrap gap-2 sm:space-x-0">
          <Button type="button" data-testid="crop-reset-btn" variant="ghost" disabled={saving || !image} onClick={reset} className="mr-auto rounded-full px-3 text-xs"><RotateCcw className="w-3.5 h-3.5 mr-1" />Reset</Button>
          <Button type="button" data-testid="crop-cancel-btn" variant="outline" disabled={saving} onClick={onCancel} className="rounded-full px-3 text-xs">Cancel</Button>
          <Button type="button" data-testid="crop-save-btn" disabled={saving || !view} onClick={save} className="rounded-full bg-ac hover:bg-ac-hover text-xs">{saving ? "Saving…" : "Use cover"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
